/**
 * The first-run intro on `/` (docs/plans/active/first-run-intro-2026-10-13.md):
 * a PostHog experiment for new guests, against today's language step.
 */
export const FIRST_RUN_INTRO_EXPERIMENT_KEY = "first_run_intro_v1";

export const FIRST_RUN_INTRO_VARIANTS = {
  control: "control",
  horizon: "horizon_intro",
} as const;

export type FirstRunIntroVariant =
  (typeof FIRST_RUN_INTRO_VARIANTS)[keyof typeof FIRST_RUN_INTRO_VARIANTS];

/** Written once a guest starts a session, signs in or opens More languages from the intro. */
export const FIRST_RUN_INTRO_SEEN_STORAGE_KEY = "chickytutor:first-run-intro:v1";
/** Where `useUserPreferences` keeps the saved language (persistent storage). */
const USER_PREFERENCES_STORAGE_KEY = "userLanguagePreferences";

/** Development only: `?first_run_intro=horizon_intro` (or `control`) skips PostHog. */
export const FIRST_RUN_INTRO_OVERRIDE_QUERY_KEY = "first_run_intro";

export function parseFirstRunIntroVariant(value: unknown): FirstRunIntroVariant | null {
  return value === FIRST_RUN_INTRO_VARIANTS.control || value === FIRST_RUN_INTRO_VARIANTS.horizon
    ? value
    : null;
}

function defaultStorage(): Storage | null {
  return typeof window === "undefined" ? null : window.localStorage;
}

export function hasSeenFirstRunIntro(storage: Storage | null = defaultStorage()): boolean {
  try {
    return Boolean(storage?.getItem(FIRST_RUN_INTRO_SEEN_STORAGE_KEY));
  } catch {
    // Unreadable storage: treat the guest as returning, so nobody sees the intro twice.
    return true;
  }
}

/**
 * A cheap guess, before preferences and auth have loaded, at whether this
 * visitor could get the intro: no intro mark, no saved language and no guest
 * practice. The loading screen uses it to leave out the classroom film, which
 * would otherwise flash before the planet. A wrong "yes" only costs a plainer
 * loading screen; unreadable storage answers "no".
 */
export function mightSeeFirstRunIntro(
  storage: Storage | null = defaultStorage(),
  guestUsageSeconds = 0
): boolean {
  if (!storage || hasSeenFirstRunIntro(storage) || guestUsageSeconds > 0) return false;
  try {
    const raw = storage.getItem(USER_PREFERENCES_STORAGE_KEY);
    if (!raw) return true;
    const saved = JSON.parse(raw) as { selectedLanguageCode?: unknown } | null;
    return !saved?.selectedLanguageCode;
  } catch {
    return false;
  }
}

export function markFirstRunIntroSeen(
  storage: Storage | null = defaultStorage(),
  nowMs = Date.now()
): void {
  try {
    storage?.setItem(FIRST_RUN_INTRO_SEEN_STORAGE_KEY, JSON.stringify({ seen_at_ms: nowMs }));
  } catch {
    // The mark is best effort; it must never block the speaking flow.
  }
}

export type FirstRunIntroCandidateInput = {
  isGuest: boolean;
  brandVariant: string;
  preSessionVariant: string;
  shellLayout: string;
  analyticsSurface: string;
  launchSurface: string;
  /** A language, level, mode, chapter, podcast or custom instructions came with the URL. */
  hasRouteContext: boolean;
  storedLanguageCode: string | null | undefined;
  guestUsageSeconds: number;
  seen: boolean;
};

/**
 * New guests on the default `/` only. Anyone who arrives with a language in
 * mind, has chosen one before, has practised or has seen the intro keeps
 * today's interface.
 */
export function isFirstRunIntroCandidate(input: FirstRunIntroCandidateInput): boolean {
  return (
    input.isGuest &&
    input.brandVariant === "default" &&
    input.preSessionVariant === "default" &&
    input.shellLayout === "fullscreen" &&
    input.analyticsSurface === "fullscreen" &&
    input.launchSurface === "standard" &&
    !input.hasRouteContext &&
    !input.storedLanguageCode &&
    input.guestUsageSeconds <= 0 &&
    !input.seen
  );
}

export function readFirstRunIntroOverride(
  search: string,
  isDevelopment: boolean
): FirstRunIntroVariant | null {
  if (!isDevelopment) return null;
  return parseFirstRunIntroVariant(
    new URLSearchParams(search).get(FIRST_RUN_INTRO_OVERRIDE_QUERY_KEY)
  );
}
